import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, GraduationCap, BookOpen, Users, Crown } from 'lucide-react'
import { courseApi, type PublicCourse } from '../api/lessonBuilderApi'
import { COURSE_LEVELS } from '../types/CourseDoc'
import StarRating from '../components/StarRating'

const levelLabel = (lv?: string) => COURSE_LEVELS.find((x) => x.value === lv)?.label || ''

// Catalog công khai: danh sách khoá đã xuất bản + lọc chủ đề/trình độ + tìm kiếm.
const CatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<PublicCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [lv, setLv] = useState('')

  useEffect(() => {
    courseApi.publicCourses().then(setCourses).finally(() => setLoading(false))
  }, [])

  const categories = useMemo(
    () => Array.from(new Set(courses.map((c) => c.category).filter(Boolean))) as string[],
    [courses]
  )
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase()
    return courses.filter((c) =>
      (!cat || c.category === cat) &&
      (!lv || c.level === lv) &&
      (!kw || `${c.title} ${c.subtitle || ''} ${c.category || ''}`.toLowerCase().includes(kw))
    )
  }, [courses, q, cat, lv])

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-blue-100 text-sm font-semibold mb-2">
            <GraduationCap size={18} /> Khoá học Vật Lí 365
          </div>
          <h1 className="text-3xl font-black">Khám phá khoá học</h1>
          <p className="text-blue-100 mt-1">Chọn khoá học phù hợp và bắt đầu hành trình của bạn.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Bộ lọc */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Tìm khoá học..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-blue-200">
            <option value="">Tất cả chủ đề</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={lv} onChange={(e) => setLv(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-blue-200">
            <option value="">Mọi trình độ</option>
            {COURSE_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">Đang tải khoá học...</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            {courses.length === 0 ? 'Chưa có khoá học nào được xuất bản.' : 'Không tìm thấy khoá học phù hợp.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/khoa-hoc/${c.slug}`)}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col text-left hover:shadow-md hover:border-blue-200 transition-all"
              >
                <div className="h-32 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center relative">
                  {c.coverImage ? (
                    <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <GraduationCap className="text-white/80" size={34} />
                  )}
                  <span className={`absolute top-2 right-2 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    c.access === 'pro' ? 'bg-amber-400 text-amber-900' : 'bg-white/90 text-emerald-700'
                  }`}>
                    {c.access === 'pro' ? <><Crown size={11} /> PRO</> : 'Miễn phí'}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    {c.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{c.category}</span>}
                    {levelLabel(c.level) && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{levelLabel(c.level)}</span>}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 leading-snug">{c.title}</h3>
                  {c.instructorName && <p className="text-[11px] text-gray-500 mt-0.5">👩‍🏫 {c.instructorName}</p>}
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2 flex-1">{c.subtitle || '—'}</p>
                  <div className="mt-2"><StarRating value={c.ratingAvg || 0} count={c.ratingCount || 0} size={14} /></div>
                  <div className="flex items-center gap-4 text-[11px] text-gray-400 mt-2">
                    <span className="flex items-center gap-1"><BookOpen size={13} /> {c.lessonCount} bài</span>
                    <span className="flex items-center gap-1"><Users size={13} /> {c.enrolledCount} học viên</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CatalogPage
